import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Door, Location, Query, Visitor, VisitorGroupsFilter } from 'graphql/generated/graphqlTypes';
import {
  GET_ALL_VISITORS_GROUPS,
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_WITHOUT_NESTED_RESOLVERS,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITORS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';
import { filterHiddenVisitorGroups } from 'v2/utils/visitorGroupOrigin';

export const refetchQueries = [
  { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_VISITORS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_DOORS_WITHOUT_NESTED_RESOLVERS }
];

type UseVisitorsGroupsCacheProps = BaseHookProps & {
  filter?: VisitorGroupsFilter;
};

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  { filter?: VisitorGroupsFilter }
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
const GET_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitors'>> =
  GET_VISITORS_WITHOUT_NESTED_RESOLVERS;
const GET_DOORS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> =
  GET_DOORS_WITHOUT_NESTED_RESOLVERS;
const GET_ALL_VISITORS_GROUPS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  { filter?: VisitorGroupsFilter }
> = GET_ALL_VISITORS_GROUPS;

const useVisitorsGroupsCache = ({ handleFetchError, filter }: UseVisitorsGroupsCacheProps) => {
  const { translation } = useTranslation();

  const handleError = (message: string) => (error: Error) => {
    handleFetchError(message);
    console.error(error);
  };

  const queryOptions = { variables: { filter } };

  // Warm up the cache — each query requests only ids for nested entities, so no nested resolver runs.
  const {
    loading: groupsWarmupLoading,
    data: groupsData,
    error: groupsWarmupError,
    refetch: refetchGroups
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, queryOptions);

  useEffect(() => {
    if (groupsWarmupError) handleError(translation.user_groups_error_fetching)(groupsWarmupError);
  }, [groupsWarmupError]);

  const {
    loading: locationsWarmupLoading,
    data: locationsData,
    error: locationsWarmupError,
    refetch: refetchLocations
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (locationsWarmupError) handleError(translation.device_group_error_fetching)(locationsWarmupError);
  }, [locationsWarmupError]);

  const {
    loading: visitorsWarmupLoading,
    data: visitorsData,
    error: visitorsWarmupError,
    refetch: refetchVisitors
  } = useQuery(GET_VISITORS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (visitorsWarmupError) handleError(translation.error_fetching_users)(visitorsWarmupError);
  }, [visitorsWarmupError]);

  const {
    loading: doorsWarmupLoading,
    data: doorsData,
    error: doorsWarmupError,
    refetch: refetchDoors
  } = useQuery(GET_DOORS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (doorsWarmupError) handleError(translation.devices_error_fetching)(doorsWarmupError);
  }, [doorsWarmupError]);

  const warmingUp =
    (groupsWarmupLoading && !groupsData) ||
    (locationsWarmupLoading && !locationsData) ||
    (visitorsWarmupLoading && !visitorsData) ||
    (doorsWarmupLoading && !doorsData);

  // Only a failure without data counts: a failed background refetch keeps the cached data, which stays valid.
  const hasVisitorGroupsFetchError = Boolean(
    (groupsWarmupError && !groupsData) ||
      (locationsWarmupError && !locationsData) ||
      (visitorsWarmupError && !visitorsData) ||
      (doorsWarmupError && !doorsData)
  );

  // Read the fully stitched shape straight from the normalized cache once warm-up is done.
  const {
    loading: composedLoading,
    data: allVisitorGroupsData,
    error: composedError
  } = useQuery(GET_ALL_VISITORS_GROUPS_TYPED, {
    variables: { filter },
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: warmingUp
  });

  useEffect(() => {
    if (composedError) handleError(translation.user_groups_error_fetching)(composedError);
  }, [composedError]);

  // The warm-up queries fetch only ids (`memberIds`, `locationIds`, `doorIds`) to avoid running nested
  // resolvers, so stitch the nested entities back in from the ones already held in the cache.
  const allVisitorGroups = useMemo(() => {
    // While warming up, or after a failed warm-up, the id-only maps are empty, so stitching would silently
    // drop nested entities — return nothing rather than incomplete data.
    if (warmingUp || hasVisitorGroupsFetchError) return [];

    // Apollo 4 types `returnPartialData` results as DeepPartial; the cache-only composed query is treated as complete.
    const groups = filterHiddenVisitorGroups(
      (allVisitorGroupsData?.getVisitorGroupsForTenant as Query['getVisitorGroupsForTenant'] | undefined) ?? []
    );
    const visitorsById = new Map((visitorsData?.getVisitors ?? []).map((visitor) => [visitor.id, visitor]));
    const locationsById = new Map(
      (locationsData?.getLocationsForTenant ?? []).map((location) => [location.id, location])
    );
    const doorsById = new Map((doorsData?.getDoorsForTenant ?? []).map((door) => [door.id, door]));

    return groups.map((group) => ({
      ...group,
      members: (group.memberIds ?? [])
        .map((memberId) => visitorsById.get(memberId))
        .filter((visitor): visitor is Visitor => Boolean(visitor)),
      accessGrants: (group.accessGrants ?? []).map((accessGrant) => ({
        ...accessGrant,
        locations: (accessGrant.locationIds ?? [])
          .map((locationId) => locationsById.get(locationId))
          .filter((location): location is Location => Boolean(location)),
        doors: (accessGrant.doorIds ?? [])
          .map((doorId) => doorsById.get(doorId))
          .filter((door): door is Door => Boolean(door))
      }))
    }));
  }, [warmingUp, hasVisitorGroupsFetchError, allVisitorGroupsData, visitorsData, locationsData, doorsData]);

  return {
    allVisitorGroups,
    loadingAllVisitorGroups: warmingUp || composedLoading,
    hasVisitorGroupsFetchError,
    // A failed retry is already reported through the warm-up errors, so the rejections are only settled here.
    retryFetchingVisitorGroups: () =>
      Promise.allSettled([refetchGroups(), refetchLocations(), refetchVisitors(), refetchDoors()])
  };
};

export default useVisitorsGroupsCache;
